import { Divider, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Loader from '@shared/components/Loader';
import { useUserProfile } from '@shared/components/UserFormDialog';
import { useIntl } from 'react-intl';
import {
  useCancelSubscriptionMutation,
  useDeleteUserMutation,
  useImportPaymentQuery,
  useInvoicesQuery,
  useSubscriptionQuery,
} from '../shared/queries';
import { useSubscriptionPlan } from '@shared/store';
import { ROUTES } from '@shared/AppRoutes';
import { useNavigate } from 'react-router-dom';
import { WorkingHoursScheduleType } from '@features/visits/schedule/entity';
import { SubscriptionPlansEnum } from '@features/subscription/entity';
import MonthlyWorkScheduleSection from '@features/profile/MonthlyWorkScheduleSection';
import { useAuthContext } from '@shared/auth/AuthContext';
import ImportContactsSection from '@features/profile/components/sections/ImportContactsSection';
import ProfileActions from '@features/profile/components/ProfileActions';
import ColorSchemeSection from '@features/profile/components/sections/ColorSchemeSection';
import InvoicesSection from '@features/profile/components/sections/InvoicesSection';
import ProfileInfoSection from '@features/profile/components/sections/ProfileInfoSection';
import SmsTemplatesSection from '@features/profile/components/sections/SmsTemplatesSection';
import RefreshCacheSection from '@features/profile/components/sections/RefreshCacheSection';
import CancellationSurveyDialog from '@features/profile/components/CancellationSurveyDialog';
import type { CancellationFeedback } from '@features/subscription/api';

export type UserData = {
  id?: string;
  name?: string;
  contactEmail?: string | null;
  bankAccount?: string;
  reviewUrl?: string;
  loyaltyPercent?: number;
  workingSchedule?: WorkingHoursScheduleType;
  avatarUrl?: string;
  publicId?: string;
  address?: string;
  bio?: string;
  phone?: string;
  allowPublicReservations?: boolean;
  preferredCurrency?: string;
};

const useMyProfileData = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();
  const { userData } = useUserProfile();
  const { data: subscription } = useSubscriptionQuery();
  const { mutate: cancelSubscription, isPending: isCancellingSubscription } =
    useCancelSubscriptionMutation();
  const { mutate: deleteUser } = useDeleteUserMutation();
  const { data: importPayment } = useImportPaymentQuery();
  const { data: invoices } = useInvoicesQuery();
  const plan = useSubscriptionPlan();
  const { isDemo } = useAuthContext();

  const isExpertPlan = plan === SubscriptionPlansEnum.EXPERT;
  const annualLocked =
    subscription?.billingPeriod === 'YEARLY' &&
    subscription.status === 'ACTIVE' &&
    new Date(subscription.endDate) > new Date();
  const autoRenew = subscription?.autoRenew ?? true;
  const subscriptionId = subscription ? subscription.id : undefined;
  const showImportSection = importPayment?.status === 'PAID';

  const handleChangeSubscription = () => {
    navigate(`/hairdresser/${ROUTES.subscription.path}?action=change`);
  };

  const handleCancelSubscription = (feedback: CancellationFeedback) => {
    if (subscriptionId) {
      cancelSubscription({ subscriptionId, feedback });
    }
  };

  const handleDeleteUser = () => {
    deleteUser();
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    isDemo,
    userData,
    invoices,
    isExpertPlan,
    subscriptionId,
    annualLocked,
    autoRenew,
    showImportSection,
    handleChangeSubscription,
    handleCancelSubscription,
    isCancellingSubscription,
    handleDeleteUser,
  };
};

const MyProfile = () => {
  const [isCancellationDialogOpen, setIsCancellationDialogOpen] = useState(false);
  const intl = useIntl();
  const {
    user,
    isAuthenticated,
    isLoading,
    userData,
    invoices,
    isExpertPlan,
    subscriptionId,
    annualLocked,
    autoRenew,
    showImportSection,
    handleChangeSubscription,
    handleCancelSubscription,
    isCancellingSubscription,
    handleDeleteUser,
    isDemo,
  } = useMyProfileData();

  if (isLoading && !isDemo) {
    return <Loader />;
  }

  if (!userData) {
    return (
      <Typography>
        {intl.formatMessage({
          id: 'myProfile.missingData',
          defaultMessage: 'Žádná data nenalezena.',
        })}
      </Typography>
    );
  }

  if ((!isAuthenticated || !user) && !isDemo) {
    return null;
  }

  return (
    <Stack spacing={2}>
      <ProfileInfoSection
        userData={userData}
        userEmail={user?.email ?? 'demo@kadernikplus.cz'}
        userSub={user?.sub ?? 'auth0|demo'}
        isExpertPlan={isExpertPlan}
      />

      <ProfileActions
        annualLocked={annualLocked}
        autoRenew={autoRenew}
        subscriptionId={subscriptionId}
        onChangeSubscription={handleChangeSubscription}
        onCancelSubscription={() =>
          annualLocked
            ? handleCancelSubscription({ reason: 'stop_annual_renewal' })
            : setIsCancellationDialogOpen(true)
        }
        onDeleteUser={handleDeleteUser}
      />
      <Divider />
      <InvoicesSection invoices={invoices} />
      <SmsTemplatesSection />
      <ColorSchemeSection />
      <MonthlyWorkScheduleSection />
      {showImportSection && <ImportContactsSection />}
      <RefreshCacheSection />
      <CancellationSurveyDialog
        open={isCancellationDialogOpen}
        isSubmitting={isCancellingSubscription}
        onClose={() => setIsCancellationDialogOpen(false)}
        onConfirm={handleCancelSubscription}
      />
    </Stack>
  );
};

export default MyProfile;
